<template>
  <section>
    <PerfEvalReadingsForm
      v-if="isReadingsFormOpen"
      v-model:selected-well-readings="selectedWellReadingsData"
      :erp-well-readings="erpWellReadings?.getErpWellReadings ?? null"
      @confirm="createNewPerfEvalFromWell"
      @cancel="handleFormCancel"
    />
    <div v-else-if="perfevals.length > 0 || createNewPerfEval">
      <div v-bind="$attrs" class="performance-evaluation wrapper">
        <div class="sub-header shadow-4">
          <header class="container py-4">
            <div class="flex align-items-center mobile-wrap">
              <div class="mr-4">
                <span class="body-1"> Scenario: </span>
                <span class="body-2">
                  {{ scenario?.title }}
                </span>
              </div>
              <div v-if="!isNewPerfeval" class="flex align-items-center">
                <span class="body-1 mr-2"> Evaluation: </span>
                <Dropdown
                  :modelValue="route.params.perfevalId"
                  :options="perfevalSwitcherOptions"
                  class="w-full dropdown-sm p-inputtext-sm"
                  optionValue="id"
                  append-to="body"
                  @change="onPerfevalDropdownChange"
                >
                  <template #option="{ option: { title } }">
                    {{ title }}
                  </template>
                  <template #value="{ value }">
                    <div class="flex align-items-center">
                      {{ getPerfevalTitle(value) }}
                    </div>
                  </template>
                </Dropdown>
              </div>
              <Button
                v-if="!isNewPerfeval"
                :disabled="!canSaveChanges"
                class="p-button-danger ml-2"
                @click="showDeleteDialog = true"
              >
                Delete Evaluation
              </Button>
              <Button
                v-if="!isNewPerfeval"
                class="p-button p-button-info text-white py-2 px-4 rounded ml-2"
                @click="isModalOpen = true"
              >
                Create New Evaluation
              </Button>
              <span
                v-if="!isNewPerfeval && isDirty"
                v-tooltip.hover="'Save changes before generating report'"
              >
                <Button :disabled="true" class="ml-2 p-button-success">
                  Generate Perf Eval Report
                </Button>
              </span>

              <Button
                v-else-if="!isDirty && !isNewPerfeval"
                class="ml-2 p-button-success"
                @click="showReportPage = !showReportPage"
              >
                {{
                  showReportPage
                    ? "Return to Perf Eval"
                    : "Generate Perf Eval Report"
                }}
              </Button>
              <span v-if="isNewPerfeval" class="preview">PREVIEW</span>
            </div>
            <div class="flex align-items-center justify-space-between">
              <p class="body-3 mr-2">
                Created By:
                {{
                  performanceEvaluation?.r_author?.display_name ||
                  performanceEvaluation?.r_author?.email ||
                  user.display_name ||
                  user.email
                }}
              </p>
              <p class="body-3">
                Saved At
                {{
                  performanceEvaluation?.journal_updated_at
                    ? format(
                        new Date(performanceEvaluation?.journal_updated_at),
                        "MMMM-dd-yyyy HH:mm:ss"
                      )
                    : "TBD"
                }}
              </p>
            </div>

            <fieldset
              :disabled="!canSaveChanges"
              class="grid align-items-center"
            >
              <FormLabel v-slot="{ id }" text="Perf Eval Title" class="col">
                <InputText
                  :id="String(id)"
                  ref="titleField"
                  class="w-full"
                  :class="{
                    'p-invalid': perfEvalForm.V?.value.title?.$invalid
                  }"
                  :model-value="perfEvalForm.data.value.title ?? ''"
                  @update:model-value="
                    perfEvalForm.update({
                      title: $event
                    })
                  "
                  @keydown.enter="onSave"
                />
              </FormLabel>
              <div class="col-fixed align-self-center">
                <div class="flex justify-content-between mx-list-2">
                  <span v-if="!!user.is_admin">
                    <Button
                      class="p-button-info"
                      label="Show Diff"
                      :disabled="!isDirty || !canSaveChanges"
                      @click="showDiff = true"
                    />
                  </span>
                  <Button
                    class="p-button-info"
                    :disabled="!isDirty || isInvalidForm || !canSaveChanges"
                    @click="onSave"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </fieldset>
          </header>
        </div>
        <PerfEvalReport
          v-if="showReportPage"
          :frequency-range="frequencyRange"
        />

        <div v-else>
          <div class="overlay-container">
            <div v-if="isNewPerfeval" class="overlay"></div>
            <div class="container my-4">
              <div class="flex justify-content-end">
                <Dropdown
                  v-model="selectedChartOption"
                  :options="chartOptions"
                  append-to="body"
                />
              </div>
            </div>

            <div class="container chart-grid mb-4">
              <div v-if="selectedChartOption === 'Pump Table'">
                <PumpCalcTable />
              </div>

              <div v-if="selectedChartOption === 'Single'">
                <SinglePumpChart
                  draw-legend
                  enable-mouse-tracking
                  hide-pump-selector
                  :minimum-height-px="300"
                  :aspect-ratio="5"
                  :npsh-mode="!isEspOnShore"
                  x-legend-label="In Situ Flow Rate"
                  y-legend-label="TDH"
                />
              </div>
              <div v-if="selectedChartOption === 'Tornado'" class="grid">
                <PumpTornadoChartSet
                  :chart-min-height="300"
                  use-evaluation-overrides
                  hide-pump-selector
                  :starting-frequency-range="frequencyRange"
                  @frequency-range-updated="onFrequencyRangeUpdated"
                />
              </div>
              <div v-if="selectedChartOption === 'Tapered'">
                <TaperedPumpsChartSet
                  :minimum-height-px="400"
                  use-evaluation-overrides
                  hide-pump-selector
                  :starting-frequency-range="frequencyRange"
                  @frequency-range-updated="onFrequencyRangeUpdated"
                />
              </div>
              <div v-if="selectedChartOption === 'Gas'">
                <GasCurveChart
                  draw-legend
                  enable-mouse-tracking
                  :minimum-height-px="300"
                  :aspect-ratio="10"
                  x-legend-label="In Situ Flow Rate"
                  y-legend-label="Req BHP"
                />
              </div>
            </div>

            <div class="container form-grid">
              <div class="perfeval-form">
                <fieldset
                  v-if="perfEvalForm.data.value"
                  :disabled="!canSaveChanges"
                  class="mb-4"
                >
                  <div class="field-grid my-4">
                    <ConverterInput
                      label="Pump Hz"
                      mode="write"
                      :base-unit="GenericUnits.Generic"
                      :decimal-places="6"
                      :update-unit="false"
                      :model="GenericUnits.options()"
                      :model-value="perfEvalForm.data.value?.pump_hz"
                      @update:model-value="
                        perfEvalForm.update({ pump_hz: $event })
                      "
                    />
                  </div>
                  <Divider>Surface</Divider>
                  <!-- eslint-disable -->
                  <WellTestSurface
                    v-model:totalLiquidFlowRateStbd="totalLiquidFlowRateStbd"
                    v-model:waterCutPercentage="waterCutPercentage"
                    v-model:oilFlowRateStbd="oilFlowRateStbd"
                    v-model:waterFlowRateStbd="waterFlowRateStbd"
                    v-model:productivityIndexStbdPsia="productivityIndexStbdPsia"
                    :well-productivity-index-stbd-psia-for-display="wellProductivityIndexStbdPsiaForDisplay"
                    v-model:gasOilRatioScfStb="gasOilRatioScfStb"
                    v-model:gasLiquidRatioScfStb="gasLiquidRatioScfStb"
                    v-model:gasFlowRateMscf="gasFlowRateMscf"
                    v-model:gasInputType="gasInputType"
                    v-model:liquidInputType="liquidInputType"
                    :form="perfEvalForm"
                  />
                  <section v-if="isEspOnShore">
                  <Divider>Pressure</Divider>
                  <div class="field-grid my-4">
                    <ConverterInput
                      v-model="perfEvalForm.data.value.tubing_pressure_psia"
                      label="Tubing Pressure"
                      :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                      :model="PressureUnits.options()"
                      :update-unit="false"
                      mode="write"
                    />
                    <ConverterInput
                      v-model="perfEvalForm.data.value.casing_pressure_psia"
                      label="Casing Pressure"
                      :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                      :model="PressureUnits.options()"
                      :update-unit="false"
                      mode="write"
                    />
                    <ConverterInput
                      v-model="
                        perfEvalForm.data.value.pump_intake_pressure_psia
                      "
                      label="Pump Intake Pressure"
                      :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                      :model="PressureUnits.options()"
                      :update-unit="false"
                      mode="write"
                    />
                  </div>
                  <GasNaturalSeparation :form="perfEvalForm" />
                  <GasIntakeSeparation
                    :form="perfEvalForm"
                    :updateScenario="false"
                  />
                  </section>
                  <section v-else>
                    <div class="grid my-4">
                      <ConverterInput
                      class="col-12 md:col-6 lg:col-3"
                      v-model="
                        perfEvalForm.data.value.pump_intake_pressure_psia
                      "
                      label="Pump Intake Pressure"
                      :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                      :model="PressureUnits.options()"
                      :update-unit="false"
                      mode="write"
                    />
                    </div>
                  </section>

                  <Divider>Pump Corrections</Divider>
                  <div class="flex align-items-center mx-list-2 mobile-wrap">
                    <ConverterInput
                      label="Pump Wear Head Correction"
                      :model-value="
                        perfEvalForm.data.value.pump_wear_head_correction_ratio
                      "
                      :base-unit="PercentageUnits.Ratio"
                      :target-unit="PercentageUnits.Percentage"
                      :model="PercentageUnits.options()"
                      mode="write"
                      :min="0"
                      @update:model-value="
                        perfEvalForm.update({
                          pump_wear_head_correction_ratio: $event
                        })
                      "
                    />
                    <ConverterInput
                      label="Pump Wear BHP Correction"
                      :model-value="
                        perfEvalForm.data.value
                          .pump_wear_brake_horsepower_correction_ratio
                      "
                      :base-unit="PercentageUnits.Ratio"
                      :target-unit="PercentageUnits.Percentage"
                      :model="PercentageUnits.options()"
                      mode="write"
                      :min="0"
                      @update:model-value="
                        perfEvalForm.update({
                          pump_wear_brake_horsepower_correction_ratio: $event
                        })
                      "
                    />
                  </div>
                  <br />
                  <div class="pumps-grid">
                    <PerfEvalPumpRow
                      v-for="(xrefPump, i) in scenarioCalculations
                        .descendingOrderPumps.value"
                      :key="xrefPump.id"
                      :index="i"
                      :equipment-xref-pump="xrefPump"
                      :perfeval-xref-pump="
                        getPerfEvalPumpByEquipmentPumpId(xrefPump)
                      "
                      :hide-add-below="i !== pumpsOrder?.length - 1"
                      :hide-remove="false"
                      :is-last="i === pumpsOrder?.length - 1"
                    />
                  </div>
                </fieldset>
              </div>
              <div class="perfeval-calcs">
                <EquipmentCalculator
                  :router-params="routeParams"
                  :update-scenario="false"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="performance-evaluation wrapper">
        <div class="sub-header shadow-4">
          <header class="container py-4">
            <div class="flex align-items-center mobile-wrap">
              <div class="mr-4">
                <span class="body-1"> Scenario: </span>
                <span class="body-2">
                  {{ scenario?.title }}
                </span>
              </div>
              <div class="flex align-items-center">
                <button
                  class="p-button p-button-info text-white font-bold py-2 px-4 rounded"
                  @click="isModalOpen = true"
                >
                  Create New Evaluation
                </button>
              </div>
            </div>
          </header>
        </div>
      </div>
    </div>
    <!-- Modal Overlay -->
    <Dialog
      v-model:visible="isModalOpen"
      header="Create Options"
      :draggable="false"
      :style="{ width: '570px' }"
      modal
    >
      <!-- Modal Content -->
      <div class="flex">
        <button
          class="p-button p-button-danger text-white font-bold my-2 px-4 rounded"
          @click="isModalOpen = false"
        >
          Cancel
        </button>
        <button
          class="ml-2 p-button p-button-info text-white font-bold my-2 px-4 rounded"
          @click="createNewEvaluation"
        >
          Create From Scenario
        </button>
        <router-link
          v-if="shouldFetchWellReadings"
          class="ml-2 p-button p-button-info text-white font-semibold my-2 px-4 rounded"
          :to="{
            params: { perfevalId: 'new' },
            query: { pageState: 'wellForm' }
          }"
          @click="
            isReadingsFormOpen = true;
            isModalOpen = false;
          "
        >
          Create From Well Data
        </router-link>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="showDeleteDialog"
      header="Prod Rates"
      :draggable="false"
      :style="{ width: '60vw' }"
      modal
    >
      <template #header>
        <h3>Confirm Deletion</h3>
      </template>

      <div class="p-4">
        <h4>
          Are you sure you want to delete this evaluation:
          {{ perfEvalForm.initialData.value?.title }}?
        </h4>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          class="p-button-text"
          @click="showDeleteDialog = false"
        />
        <Button
          label="Delete Evaluation"
          class="p-button-danger"
          @click="onDelete"
        />
      </template>
    </Dialog>
    <Dialog
      v-model:visible="showDiff"
      header="Form Diff"
      :draggable="false"
      :style="{ width: '60vw' }"
      modal
    >
      <div class="p-4">
        <vue-json-pretty
          :data="{
            performanceEvaluation: perfEvalForm.diff.value,
            xrefPumps: perfEvalPumpsForm.diff.value
          }"
        ></vue-json-pretty>
      </div>
    </Dialog>
  </section>
</template>

<script setup lang="ts">
import type { RouteParams, RouteQueryAndHash } from "vue-router";
import { useRouter, useRoute } from "vue-router";
import { required } from "@vuelidate/validators";
import { format } from "date-fns";
import { PercentageUnits } from "@/libs/units/percentage";
import type { ErpWellReadings } from "gql/api";
import {
  ErpWellReadingsIdType,
  Sizing_Pumping_Type_Enum,
  useErpWellReadingsQuery,
  useInsertPerformanceEvaluationRevisionMutation,
  Well_Test_Liquid_Input_Options_Enum
} from "gql/api";
import {
  injectStrict,
  isEmptyObject,
  isNullOrUndefinedOrEmptyString,
  mapKeys
} from "@/utils/utils";
import { restoreRefsOnMutationError } from "@/utils/utils-dependent";
import { PressureUnits } from "@/libs/units/pressure";
import { GenericUnits } from "@/libs/units/generic";
import { onMounted, computed, provide, ref, watch } from "vue";
import SinglePumpChart from "@/components/scenarios/charts/SinglePumpChart.vue";
import PumpTornadoChartSet from "@/components/scenarios/charts/PumpTornadoChartSet.vue";
import PumpCalcTable from "@/components/scenarios/PumpCalcTable.vue";
import GasCurveChart from "@/components/scenarios/charts/GasCurveChart.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import PerfEvalReport from "./PerfEvalReport.vue";
import FormLabel from "@/components/FormLabel.vue";
import Dialog from "primevue/dialog";
import type { DropdownChangeEvent } from "primevue/dropdown";

import useForm from "@/composables/forms/useForm";
import { useWellTestInput } from "@/composables/forms/useWellTestInput";
import { haltUntilCompletion } from "@/composables/exchanges/mutationLimiter";
import {
  perfevalsKey,
  scenarioCalculationsKey,
  scenarioKey,
  equipmentKey,
  parametersKey,
  wellPropertiesKey,
  perfevalFormKey,
  perfevalPumpsFormKey,
  perfevalPumpsMapKey,
  sizingKey,
  pumpsMapKey,
  sizingFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import useEmptyPerfeval from "@/composables/useEmptyPerfeval";
import type { WellProperties, XrefPumps } from "@/composables/forms/formTypes";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import PerfEvalPumpRow from "@/components/performanceEvaluations/PerfEvalPumpRow.vue";
import type { SpyglassUser } from "@/composables/auth/useUserInfo";
import useUserInfo from "@/composables/auth/useUserInfo";
import useDestructureScenario from "@/composables/useDestructureScenario";
import EquipmentCalculator from "@/components/scenarios/calculator_sections/EquipmentCalculator.vue";
import { useOnline } from "@vueuse/core";
import { useMutationNotification } from "@/composables/logger/mutationNotification";
import { getIntervalAround } from "@/composables/useDefaultPumpFrequencyRange";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import WellTestSurface from "./wellInfo/WellTestSurface.vue";
import GasNaturalSeparation from "@/components/scenarios/GasNaturalSeparation.vue";
import GasIntakeSeparation from "@/components/scenarios/GasIntakeSeparation.vue";
import useCreatePerfEvalFromWell from "@/composables/useCreatePerfEvalFromWell";
import type { Maybe } from "graphql/jsutils/Maybe";
import PerfEvalReadingsForm from "@/components/performanceEvaluations/PerfEvalReadingsForm.vue";
import { useToast } from "primevue/usetoast";
import TaperedPumpsChartSet from "@/components/scenarios/charts/TaperedPumpsChartSet.vue";
import { useEvaluationStore } from "@/store";

const perfEvalStore = useEvaluationStore();

const props = defineProps<{
  sizingId: string;
  scenarioId: string;
  routeParams: RouteParams;
  routeQuery?: RouteQueryAndHash;
}>();

const { registerResultToast } = useMutationNotification();

const toaster = useToast();

const showDiff = ref(false);

const scenario = injectStrict(scenarioKey);
const perfevals = injectStrict(perfevalsKey);
const sizing = injectStrict(sizingKey);
const sizingForm = injectStrict(sizingFormKey, null);

const router = useRouter();
const route = useRoute();

const showDeleteDialog = ref(false);
const isModalOpen = ref(false);
const createNewPerfEval = ref(false);
const isReadingsFormOpen = ref(false);

const titleField = ref();
const showReportPage = ref(!!props?.routeQuery?.query?.report);
const isOnline = useOnline();

// redirect any hps scenarios for now
if (scenario.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps) {
  router.push({
    name: "ScenarioInfo",
    params: {
      sizingId: route.params.sizingId,
      scenarioId: route.params.scenarioId
    }
  });
}

const {
  equipment,
  parameters,
  wellProperties,
  correlations,
  pumps,
  intakes,
  housings,
  cables,
  seals,
  deviationSurvey,
  yToolBypassTubings,
  yToolEquipmentTubings
} = useDestructureScenario(scenario);

const scenarioCalculations = useScenarioCalculations({
  scenario,
  equipment,
  wellProperties,
  parameters,
  correlations,
  pumps,
  seals,
  intakes,
  housings,
  cables,
  deviationSurvey,
  yToolBypassTubings,
  yToolEquipmentTubings
});

const isNewPerfeval = computed(() => route.params.perfevalId === "new");

const performanceEvaluation = computed(() => {
  return perfevals.value.find(pf => pf.id === route.params.perfevalId);
});

const perfEvalXrefPumps = computed(() =>
  mapKeys(performanceEvaluation.value?.r_xref_pumps || [], item => item.id)
);

const perfEvalForm = useForm(performanceEvaluation, {
  title: {
    required
  }
});

const isInvalidForm = computed(() => perfEvalForm.V?.value.$invalid);

const perfEvalPumpsForm = useForm(perfEvalXrefPumps);

const createEmptyPerfEval = useEmptyPerfeval(scenario, scenarioCalculations);

const setEmptyPerfEval = () => {
  const emptyPerfeval = createEmptyPerfEval();
  const emptyPerfevalPumps = mapKeys(
    emptyPerfeval.r_xref_pumps,
    item => item.id
  );
  perfEvalForm.update(emptyPerfeval);
  perfEvalPumpsForm.update(emptyPerfevalPumps);
};

if (isNewPerfeval.value) {
  perfEvalForm.reset();
  perfEvalPumpsForm.reset();
  setEmptyPerfEval();
  if (route.query.pageState === "wellForm") {
    isReadingsFormOpen.value = true;
    isModalOpen.value = false;
  }
}

const {
  gasFlowRateMscf,
  oilFlowRateStbd,
  waterFlowRateStbd,
  totalLiquidFlowRateStbd,
  productivityIndexStbdPsia,
  wellProductivityIndexStbdPsiaForDisplay,
  waterCutPercentage,
  gasOilRatioScfStb,
  gasLiquidRatioScfStb
} = useWellTestInput(wellProperties, parameters, perfEvalForm);

const liquidInputType = ref(
  wellProperties.value?.well_test_liquid_input_type ===
    Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
    ? Well_Test_Liquid_Input_Options_Enum.TotalRateWaterCut
    : wellProperties.value?.well_test_liquid_input_type
);

const gasInputType = ref(wellProperties.value?.well_test_gas_input_type);

watch(
  () => route.params.perfevalId,
  newPerfevalId => {
    if (newPerfevalId && newPerfevalId !== "new") {
      perfEvalStore.lastViewedId.value = newPerfevalId as string;
    }
  },
  {
    immediate: true
  }
);

onMounted(() => {
  if (performanceEvaluation.value) {
    // Focus the title field of the perf eval
    titleField.value.$el.focus();

    // Update a new perf eval with scenario changes
    perfEvalForm.reset();
    perfEvalPumpsForm.reset();
    if (isNewPerfeval.value) {
      setEmptyPerfEval();
    }
  }
});

const modifiedParameters = computed(() => {
  if (parameters.value) {
    return {
      ...parameters.value,
      target_liquid_rate_std_barrel_day: totalLiquidFlowRateStbd.value,
      bottom_casing_pressure_psia:
        perfEvalForm.data.value?.casing_pressure_psia,
      bottom_tubing_pressure_psia:
        perfEvalForm.data.value?.tubing_pressure_psia,
      pump_hz: perfEvalForm.data.value?.pump_hz,
      intake_override_gas_separation_percent:
        perfEvalForm.data.value?.intake_override_gas_separation_percent,
      intake_gas_separation_override_type:
        perfEvalForm.data.value?.intake_gas_separation_override_type,
      natural_override_gas_separation_percent:
        perfEvalForm.data.value?.natural_override_gas_separation_percent,
      natural_gas_separation_override_type:
        perfEvalForm.data.value?.natural_gas_separation_override_type,
      pump_wear_brake_horsepower_correction_ratio:
        perfEvalForm.data.value?.pump_wear_brake_horsepower_correction_ratio,
      pump_wear_head_correction_ratio:
        perfEvalForm.data.value?.pump_wear_head_correction_ratio
    };
  }
  return null;
});

const modifiedWellProperties = computed<WellProperties>(() => {
  if (wellProperties.value) {
    return {
      ...wellProperties.value,
      well_test_water_rate_stb_day: waterFlowRateStbd.value,
      well_test_gas_rate_mscf_day: gasFlowRateMscf.value,
      well_test_oil_rate_stb_day: oilFlowRateStbd.value
    };
  }
  return null;
});

const modifiedPumps = computed<Record<string, XrefPumps[number]>>(
  () =>
    Object.values(pumps.value).reduce(
      (acc, item) =>
        Object.assign(acc, {
          [item.id]: {
            ...item,
            head_correction_ratio:
              getPerfEvalPumpByEquipmentPumpId(item)?.head_correction_ratio,
            rate_correction_ratio:
              getPerfEvalPumpByEquipmentPumpId(item)?.rate_correction_ratio,
            brake_horsepower_correction_ratio:
              getPerfEvalPumpByEquipmentPumpId(item)
                ?.brake_horsepower_correction_ratio
          }
        }),
      {}
    ) || []
);

const pumpsOrder = computed(() => equipment.value?.pumps_order || []);

const overrideScenarioCalculations = useScenarioCalculations(
  {
    scenario,
    equipment,
    wellProperties: modifiedWellProperties,
    parameters: modifiedParameters,
    correlations,
    pumps: modifiedPumps,
    seals,
    intakes,
    housings,
    cables,
    deviationSurvey,
    yToolBypassTubings,
    yToolEquipmentTubings
  },
  {
    pumpIntakePressurePSIA: computed(
      () => perfEvalForm.data.value.pump_intake_pressure_psia
    )
  }
);

provide(perfevalFormKey, perfEvalForm);
provide(perfevalPumpsFormKey, perfEvalPumpsForm);
provide(perfevalPumpsMapKey, modifiedPumps);
provide(pumpsMapKey, modifiedPumps);

// Overwriting existing scenario for child components with perf eval changes
provide(scenarioCalculationsKey, overrideScenarioCalculations);
provide(equipmentKey, equipment);
provide(parametersKey, modifiedParameters);
provide(wellPropertiesKey, modifiedWellProperties);

// const { frequencyRange } = useDefaultPumpFrequencyRange(
//   overrideScenarioCalculations.pumpHz,
//   {
//     reactiveUpdates: false
//   }
// );
const startingRange = getIntervalAround(
  overrideScenarioCalculations.pumpHz.value,
  2,
  5,
  30,
  80
);
const frequencyRange = ref<[number, number]>(startingRange);
const onFrequencyRangeUpdated = (newFrequencyRange: [number, number]) => {
  frequencyRange.value = newFrequencyRange;
};

const isDirty = computed(
  () =>
    !isEmptyObject(perfEvalForm.diff.value) ||
    !isEmptyObject(perfEvalPumpsForm.diff.value)
);

const user = useUserInfo() as SpyglassUser;

const { isImportedFromLegacy } = useEditPermissions();

const canSaveChanges = computed(
  () =>
    (user.is_admin ||
      route.params.perfevalId === "new" ||
      user.id === perfEvalForm.data.value.journal_author_id) &&
    !isImportedFromLegacy.value
);

const getPerfEvalPumpByEquipmentPumpId = (
  equipmentXrefPump: XrefPumps[number]
) =>
  Object.values(perfEvalPumpsForm.data.value).find(
    item => item.xref_pump_id === equipmentXrefPump.id
  );

const { executeMutation } = useInsertPerformanceEvaluationRevisionMutation();

async function onDelete() {
  perfEvalForm.reset();

  perfEvalForm.update({
    journal_deleted_at: "now()"
  });

  const result = executeMutation({
    update_performance_evaluations: true,
    update_pumps: false,
    performance_evaluations_revisions: [perfEvalForm.prepare()],
    performance_evaluations_xref_pumps_revisions: []
  });

  restoreRefsOnMutationError(result, perfEvalForm.data, perfEvalPumpsForm.data);
  registerResultToast("Performance Evaluation Delete", result);

  await haltUntilCompletion();

  showDeleteDialog.value = false;
  createNewPerfEval.value = false;
}

async function onSave() {
  const perfevalRevision = perfEvalForm.prepare();
  const pumpsRevisions = perfEvalPumpsForm.prepareDiffsAsArray();

  const result = executeMutation({
    update_performance_evaluations: true,
    update_pumps: true,
    performance_evaluations_revisions: [perfevalRevision],
    performance_evaluations_xref_pumps_revisions: pumpsRevisions
  });

  restoreRefsOnMutationError(result, perfEvalForm.data, perfEvalPumpsForm.data);
  registerResultToast("Performance Evaluation Update", result);

  await haltUntilCompletion();

  // dont reroute if mutation returned an error
  const online = useOnline();
  if (online.value) {
    const { error } = await result;
    if (error) return;
  }
  perfEvalForm.reset();
  perfEvalPumpsForm.reset();
  createNewPerfEval.value = false;

  if (isNewPerfeval.value) {
    await router.push({
      name: "PerformanceEvaluation",
      params: {
        sizingId: route.params.sizingId,
        scenarioId: perfevalRevision.scenario_id as string,
        perfevalId: perfevalRevision.journal_id as string
      }
    });
  }
}

const shouldFetchWellReadings = computed(
  () => isOnline && sizing.value?.well_id
);

ref<ErpWellReadings>();
const erpWellId = ref("");
const erpWellReadingsIdType = ref(ErpWellReadingsIdType.ErpWelladdressId);
const { data: erpWellReadings } = useErpWellReadingsQuery({
  variables: computed(() => ({
    id: erpWellId.value,
    id_type: erpWellReadingsIdType
  })),
  pause: !shouldFetchWellReadings.value
});

const selectedWellReadingsData = ref<ErpWellReadings>({
  __typename: "ErpWellReadings"
});

const address_erp_id = sizing.value?.well?.address_erp_id;
const well_erp_id = sizing.value?.well?.well_erp_id;
if (address_erp_id) {
  erpWellReadingsIdType.value = ErpWellReadingsIdType.ErpShipToId;
  erpWellId.value = address_erp_id;
} else if (well_erp_id) {
  erpWellReadingsIdType.value = ErpWellReadingsIdType.ErpWellId;
  erpWellId.value = well_erp_id;
}

function setHPCorrectionRatio(targetMotorA: Maybe<number>) {
  if (!targetMotorA) return;

  let lowCorrection = 0;
  let highCorrection = 2;

  const currentMotorA = computed(
    () => overrideScenarioCalculations.motorCalculations.motorCurrentA.value
  );

  let sanityCount = 200;
  while (targetMotorA !== currentMotorA.value) {
    if (--sanityCount < 0) {
      toaster.add({
        life: 10000,
        severity: "warn",
        summary: "BHP Correction not found",
        detail: "Unable to calculate correct BHP Correction for well"
      });
      break;
    }

    const newCorrection = (lowCorrection + highCorrection) / 2;
    perfEvalForm.update({
      pump_wear_brake_horsepower_correction_ratio: newCorrection
    });

    if (currentMotorA.value > targetMotorA) {
      highCorrection = newCorrection;
    } else {
      lowCorrection = newCorrection;
    }
  }
}

function setPumpHeadCorrectionRatio() {
  let lowCorrection = 0;
  let highCorrection = 2;

  const requiredTDH = computed(
    () =>
      overrideScenarioCalculations.pumpCalculations.aggregate
        .requiredTotalDynamicHeadFt.value
  );

  const producedTDH = computed(
    () =>
      overrideScenarioCalculations.pumpCalculations.aggregate
        .producedTotalDynamicHeadFt.value
  );

  let sanityCount = 100;
  while (requiredTDH.value.toFixed(3) !== producedTDH.value.toFixed(3)) {
    if (--sanityCount < 0) {
      toaster.add({
        life: 10000,
        severity: "warn",
        summary: "Pump Head Correction not found",
        detail: "Unable to calculate correct Head Correction for pump"
      });
      break;
    }

    const newCorrection = (highCorrection + lowCorrection) / 2;
    perfEvalForm.update({ pump_wear_head_correction_ratio: newCorrection });
    if (producedTDH.value < requiredTDH.value) {
      lowCorrection = newCorrection;
    } else {
      highCorrection = newCorrection;
    }
  }
}

async function createNewPerfEvalFromWell() {
  router.push({ query: {} });
  isReadingsFormOpen.value = false;
  await createNewEvaluation();

  if (!scenario.value) return;

  const perfevalFromWell = useCreatePerfEvalFromWell(
    selectedWellReadingsData.value,
    scenario.value,
    scenarioCalculations
  );
  if (!perfevalFromWell) return;

  perfEvalPumpsForm.data.value = {};
  for (const xrefPump of perfevalFromWell.r_xref_pumps) {
    perfEvalPumpsForm.update({ [xrefPump.id]: xrefPump });
  }
  perfEvalForm.update(perfevalFromWell);
  setHPCorrectionRatio(selectedWellReadingsData.value?.motor_current);
  setPumpHeadCorrectionRatio();
}

async function createNewEvaluation() {
  // Close the modal first
  isModalOpen.value = false;
  createNewPerfEval.value = true;

  await router.push({ params: { perfevalId: "new" } });
}

function handleFormCancel() {
  isReadingsFormOpen.value = false;
  // not a new perfeval, just closing the form should be fine since route params are already correct.
  if (!isNewPerfeval.value) {
    return;
  }
  createNewPerfEval.value = false;
  if (!perfevals.value?.length) return;

  // deep copy perfevals array (to prevent modifying original array) then sort it so the last viewed one is first, or keep original order.
  const allEvals = [...perfevals.value];

  const isLastViewed = (peval: (typeof perfevals.value)[number]) =>
    // use shallow equality check to see if the id is the same since id can hypothetically maybe be a number one day,
    // and lastViewedPerfEvalId will be a string since it comes from a router param, so it's either this or stringifying it
    !isNullOrUndefinedOrEmptyString(peval.id) &&
    peval.id === perfEvalStore.lastViewedId.value;

  allEvals.sort(e => (e.id === isLastViewed(e) ? -1 : 0));

  const backToEval = allEvals[0];

  router.replace({
    params: {
      ...route.params,
      perfevalId: backToEval.id
    }
  });
}

const onPerfevalDropdownChange = (event: DropdownChangeEvent | Event) => {
  if (!("value" in event)) return;
  router.push({ params: { perfevalId: event.value } });
};

const perfevalSwitcherOptions = computed(() => {
  return [...(perfevals.value ?? [])];
});

function getPerfevalTitle(match: string) {
  if (perfevalSwitcherOptions.value?.length) {
    return perfevalSwitcherOptions.value.find(s => s.id === match)?.title;
  }
  return "";
}

const selectedChartOption = ref("Pump Table");
const chartOptions = ref(["Pump Table", "Single", "Tornado", "Tapered", "Gas"]);

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});
</script>

<style lang="scss" scoped>
@media print {
  .sub-header {
    display: none;
  }
}

.performance-evaluation {
  background: var(--surface-50);
  .sub-header {
    background: var(--surface-350);
  }

  .chart-grid {
    padding-bottom: 1em;
    display: grid;
    gap: 1em;

    > div {
      box-shadow: var(--shadow-main);
      padding: 1.5em 1.5em 0em 1em;
      background-color: var(--surface-300);
      border-radius: 0.25em;
    }
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas: "form" "calcs";

    .perfeval-form {
      grid-area: form;
    }
    .perfeval-calcs {
      grid-area: calcs;
    }

    @include bp("desktop") {
      grid-template-columns: 75% 1fr;
      grid-template-areas: "form calcs";
      .perfeval-calcs {
        padding: 1em;
      }
    }
  }
}

@media only screen and (max-width: 750px) {
  .mobile-wrap {
    flex-wrap: wrap;
    gap: 1rem;
  }
}

.preview {
  background-color: #c53030;
  color: white;
  font-weight: bold;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  margin-left: 0.5rem;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.overlay-container {
  position: relative;
  padding-top: 1px;
}
.two-up {
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
}
</style>
